/*
 * The BetterFX page's hands-on workspace (sections/betterfx-try.liquid): a
 * Program Monitor over a timeline, with the BetterFX search bar on top.
 *
 * Select clips (click; shift/⌘/Ctrl-click or touch to add), open BetterFX
 * with ⌥Space / Alt+Space or the Open BetterFX button, type, then Enter or
 * click a result. The effect lands on the selected clips of its kind (video
 * effects and presets on video clips, audio effects on audio clips), the
 * clip's fx badge turns yellow and the monitor shows it. Undo and Reset sit
 * in the timeline's header.
 *
 * Search and ranking are ported from the plugin (betterfx-desktop/src/
 * search.ts and ranking.ts): exact, prefix, word prefix, initials ("gb"),
 * contains, then a loose letters-in-order match, with favorites, recent
 * effects and how often an effect is used breaking ties. An empty search
 * lists Recent effects. Results group by kind with the plugin's category
 * rows, like the real window (360 px wide, 5 rows before it scrolls).
 *
 * When the workspace first comes into view a pointer shows four searches,
 * then hands over ("Your turn"). Any touch stops the tour. It only runs
 * while on screen; with reduced motion it hands over at once.
 */
(() => {
  const still = window.matchMedia('(prefers-reduced-motion: reduce)');
  const STOP = {};
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
  const platform = (navigator.userAgentData && navigator.userAgentData.platform) || navigator.platform || navigator.userAgent;
  const isMac = /mac|iphone|ipad|ipod/i.test(platform);
  const WIN = { '⌘': 'Ctrl', '⌃': 'Ctrl', '⌥': 'Alt', '⇧': 'Shift' };
  // Running text: "press ⌥Space" reads "press Alt+Space" on Windows.
  const forOs = (text) =>
    isMac ? text : text.replace(/([⌃⌥⇧⌘]+)(\S)/g, (m, mods, key) => [...mods].map((c) => WIN[c]).concat(key).join('+'));

  /* ---------- The plugin's search (search.ts) ---------- */
  const normalizeSearch = (value) =>
    value
      .toLocaleLowerCase()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .replace(/[^\p{L}\p{N}\s]/gu, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  const searchWords = (value) => normalizeSearch(value).split(' ').filter(Boolean);
  const searchInitials = (value) => searchWords(value).map((w) => w[0] || '').join('');

  const scoreSubsequence = (query, text) => {
    let qi = 0;
    let prev = -1;
    let gap = 0;
    let run = 0;
    for (let ti = 0; ti < text.length; ti += 1) {
      if (text[ti] !== query[qi]) continue;
      if (prev >= 0 && ti === prev + 1) run += 25;
      else if (prev >= 0) gap += ti - prev - 1;
      else gap += ti;
      prev = ti;
      qi += 1;
      if (qi === query.length) return 5000 + run - gap * 10 - text.length;
    }
    return -1;
  };

  const scoreMatch = (query, text) => {
    const q = normalizeSearch(query);
    const t = normalizeSearch(text);
    if (!q.length) return 100;
    if (t === q) return 10000;
    if (t.startsWith(q)) return 9000 - t.length;
    const words = searchWords(t);
    const wi = words.findIndex((w) => w.startsWith(q));
    if (wi >= 0) return 8500 - wi * 50 - words[wi].length;
    const initials = searchInitials(t);
    if (initials === q) return 8300;
    if (initials.startsWith(q)) return 8200 - initials.length;
    const ci = t.indexOf(q);
    if (ci >= 0) return 7000 - ci * 10 - t.length;
    return scoreSubsequence(q, t);
  };

  const fuzzySearch = (items, query) =>
    items
      .map((item, index) => ({ item, index, score: scoreMatch(query, item.name) }))
      .filter((r) => r.score >= 0)
      .sort((a, b) => (b.score !== a.score ? b.score - a.score : a.index - b.index))
      .map((r) => r.item);

  /* ---------- The plugin's ranking (ranking.ts) ---------- */
  const normalizeRank = (value) => value.toLocaleLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '').trim();
  const rankWords = (value) => normalizeRank(value).split(/[\s\-_/\\()[\].]+/).filter(Boolean);
  const rankInitials = (value) => rankWords(value).map((w) => w[0] || '').join('');

  const queryBonus = (name, query) => {
    const n = normalizeRank(name);
    const q = normalizeRank(query);
    if (!q) return 0;
    if (n === q) return 100000;
    if (n.startsWith(q)) return 80000;
    const words = rankWords(name);
    if (words.some((w) => w === q)) return 70000;
    if (words.some((w) => w.startsWith(q))) return 60000;
    const initials = rankInitials(name);
    if (initials === q) return 55000;
    if (initials.startsWith(q)) return 50000;
    if (n.includes(q)) return 40000;
    return 0;
  };

  const KIND_ORDER = { preset: 0, video: 1, audio: 2 };

  const rankEffects = (effects, rawQuery, meta) => {
    const query = rawQuery.trim();
    const fuzzy = fuzzySearch(effects, query);
    const ranked = fuzzy.map((effect, fuzzyIndex) => {
      const recentIndex = meta.recents.indexOf(effect.name);
      const uses = meta.usage[effect.name] || 0;
      const score =
        queryBonus(effect.name, query) +
        Math.max(0, fuzzy.length - fuzzyIndex) * 1000 +
        (meta.favorites.includes(effect.name) ? 12000 : 0) +
        (recentIndex >= 0 ? Math.max(0, 5000 - recentIndex * 250) : 0) +
        (uses > 0 ? Math.min(Math.log2(uses + 1) * 40, 2000) : 0);
      return { effect, score, fuzzyIndex };
    });
    ranked.sort((a, b) => {
      if (a.score !== b.score) return b.score - a.score;
      if (a.fuzzyIndex !== b.fuzzyIndex) return a.fuzzyIndex - b.fuzzyIndex;
      if (a.effect.kind !== b.effect.kind) return KIND_ORDER[a.effect.kind] - KIND_ORDER[b.effect.kind];
      return a.effect.name.localeCompare(b.effect.name, undefined, { sensitivity: 'base', numeric: true });
    });
    return ranked.map((r) => r.effect);
  };

  const CATEGORY = { video: 'VIDEO EFFECTS', audio: 'AUDIO EFFECTS', preset: 'CUSTOM PRESETS' };

  /* ---------- What's installed in this Premiere ----------
     fx says how an effect shows in the monitor: a CSS filter, a flip or zoom,
     a crop, a layer over the picture, or a move that plays once. */
  const P = (name, fx) => ({ name, kind: 'preset', media: 'video', fx });
  const V = (name, fx, thirdParty) => ({ name, kind: 'video', media: 'video', fx: fx || {}, thirdParty: !!thirdParty });
  const A = (name) => ({ name, kind: 'audio', media: 'audio', fx: {} });
  const EFFECTS = [
    P('01. Bouncy Slide in (Left)', { anim: 'slide' }),
    P('02. Punch Zoom', { anim: 'punch', zoom: 1.1 }),
    P('Cinematic Bars', { layer: 'bars' }),
    P('Soft Vignette', { layer: 'vignette' }),
    P('Text Pop', { anim: 'pop' }),
    V('Alpha Glow'),
    V('Basic 3D'),
    V('BCC Film Grain', { layer: 'grain', filter: 'sepia(0.18) contrast(1.06)' }, true),
    V('Black & White', { filter: 'grayscale(1)' }),
    V('Brightness & Contrast', { filter: 'brightness(1.12) contrast(1.28)' }),
    V('Camera Blur', { filter: 'blur(4px)' }),
    V('Channel Blur', { filter: 'blur(2px) saturate(1.5)' }),
    V('Color Balance (HLS)', { filter: 'hue-rotate(18deg) saturate(1.2)' }),
    V('Corner Pin'),
    V('Crop', { clip: 'inset(0 14%)' }),
    V('Directional Blur', { filter: 'blur(3px)' }),
    V('Drop Shadow'),
    V('Gaussian Blur', { filter: 'blur(7px)' }),
    V('Horizontal Flip', { sx: -1 }),
    V('Invert', { filter: 'invert(1)' }),
    V('Lens Distortion', { zoom: 1.06 }),
    V('Lens Flare', { layer: 'glow' }),
    // BetterFX adds Lumetri Color as Premiere does: blank, no look until you grade
    V('Lumetri Color'),
    V('Mirror'),
    V('Mosaic', { layer: 'mosaic' }),
    V('Noise', { layer: 'grain' }),
    V('Posterize', { filter: 'contrast(1.6) saturate(1.4)' }),
    V('S_Glow', { layer: 'glow', filter: 'brightness(1.08)' }, true),
    V('S_Shake', { anim: 'shake' }, true),
    V('Sharpen', { filter: 'contrast(1.16) saturate(1.06)' }),
    V('Tint', { filter: 'grayscale(1) sepia(0.6)' }),
    V('Transform', { zoom: 1.14 }),
    V('Twirl'),
    V('Ultra Key'),
    V('Unsharp Mask', { filter: 'contrast(1.1)' }),
    V('Vertical Flip', { sy: -1 }),
    V('Warp Stabilizer'),
    V('Wave Warp'),
    A('Amplify'),
    A('DeEsser'),
    A('DeHummer'),
    A('DeNoise'),
    A('DeReverb'),
    A('Dynamics'),
    A('Hard Limiter'),
    A('Multiband Compressor'),
    A('Parametric Equalizer'),
    A('Pitch Shifter'),
    A('Studio Reverb'),
  ];
  const byName = (name) => EFFECTS.find((e) => e.name === name);

  const META = () => ({
    recents: ['S_Shake', 'Soft Vignette', 'DeNoise', '01. Bouncy Slide in (Left)', 'Crop'],
    usage: { Crop: 14, 'S_Shake': 6, DeNoise: 9, 'Warp Stabilizer': 4, '01. Bouncy Slide in (Left)': 5, 'Gaussian Blur': 3 },
    favorites: [],
  });

  const SPAN = 12;
  const CLIPS = () => [
    { id: 1, kind: 'video', track: 1, start: 0.3, end: 3.8, label: 'Interview', media: 'interview', link: 4, fx: [] },
    { id: 2, kind: 'video', track: 1, start: 3.8, end: 8.2, label: 'Surf', media: 'broll', link: 5, fx: [] },
    { id: 3, kind: 'video', track: 1, start: 8.2, end: 11.6, label: 'Mountains', media: 'outro', fx: [] },
    // a text layer (Essential Graphics) over the Surf shot
    { id: 7, kind: 'video', track: 2, start: 4.4, end: 7.6, label: 'Title', title: true, color: 'var(--bt-graphic)', fx: [] },
    { id: 4, kind: 'audio', track: 1, start: 0.3, end: 3.8, label: 'Interview', link: 1, fx: [] },
    { id: 5, kind: 'audio', track: 1, start: 3.8, end: 8.2, label: 'Surf', link: 2, fx: [] },
    { id: 6, kind: 'audio', track: 2, start: 0.3, end: 11.6, label: 'Music', color: 'var(--bt-music)', fx: [] },
  ];

  const pct = (t) => ((t / SPAN) * 100).toFixed(3);
  const timecode = (t) => {
    const frames = Math.round(t * 24);
    const s = Math.floor(frames / 24);
    const f = frames % 24;
    const two = (n) => String(n).padStart(2, '0');
    return `00:00:${two(s)}:${two(f)}`;
  };

  const init = (root) => {
    if (root.dataset.btReady) return;
    root.dataset.btReady = 'true';

    const section = root.closest('section');
    const q = (sel) => root.querySelector(sel);
    const stage = q('[data-bt-stage]');
    if (!stage) return;
    const screen = q('[data-bt-screen]');
    const picture = q('[data-bt-picture]');
    const mover = q('[data-bt-move]');
    const titleEl = q('[data-bt-title]');
    const titleMover = q('[data-bt-titlemove]');
    const titleName = titleEl ? titleEl.textContent.trim() : '';
    const videos = [...root.querySelectorAll('.bt-video')];
    const tracksEl = q('[data-bt-tracks]');
    const body = q('[data-bt-tlbody]');
    const timecodeEl = q('[data-bt-timecode]');
    const statusEl = q('[data-bt-status]');
    const openBtn = q('[data-bt-open]');
    const undoBtn = q('[data-bt-undo]');
    const resetBtn = q('[data-bt-reset]');
    const bar = q('[data-bt-bar]');
    const input = q('[data-bt-input]');
    const results = q('[data-bt-results]');
    const keysEl = q('[data-bt-keys]');
    const cursor = q('[data-bt-cursor]');
    const hintBar = q('[data-bt-barhint]');
    const doneBar = q('[data-bt-bardone]');
    const doneName = q('[data-bt-donename]');
    const doneText = q('[data-bt-donetext]');
    const donePill = q('[data-bt-donepill]');
    const tourEl = q('[data-bt-tour]');
    const tourBtn = q('[data-bt-tourbtn]');
    const dots = [...root.querySelectorAll('.bt-dots li')];

    // Words the script writes, in the page's language (sections/betterfx-try.liquid);
    // English stands in for anything missing. [effect], [clip], [count] and [query]
    // are filled in here.
    let words = {};
    try {
      const wordsEl = q('[data-bt-i18n]');
      words = wordsEl ? JSON.parse(wordsEl.textContent) || {} : {};
    } catch (e) {
      words = {};
    }
    const tr = (key, en) => (typeof words[key] === 'string' && words[key]) || en;
    const fillIn = (text, values) => text.replace(/\[(\w+)\]/g, (m, k) => (k in values ? String(values[k]) : m));

    const touch = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
    if (touch && section) section.classList.add('is-touch');
    root.querySelectorAll('[data-bt-os]').forEach((el) => (el.textContent = forOs(el.textContent)));
    const openKeys = q('[data-bt-openkeys]');
    if (openKeys) {
      if (touch) openKeys.remove();
      else if (!isMac) openKeys.innerHTML = '<kbd>Alt</kbd><kbd>Space</kbd>';
    }

    /* ---------- State ---------- */
    // the title clip is named after the text it holds
    const named = (list) => list.map((c) => (c.title && titleName ? { ...c, label: titleName } : c));
    let clips = named(CLIPS());
    let meta = META();
    let sel = new Set();
    let gap = false; // the playhead is between clips: the monitor is black
    let scrubbing = false;
    let ph = 1.5;
    let history = [];
    let list = []; // the results on show
    let selected = 0;
    let isOpen = false;
    let visible = false;
    let touring = false;
    let toured = false;
    let run = 0;
    let pointerInside = false;
    let doneTimer = 0;
    let statusTimer = 0;

    const clipById = (id) => clips.find((c) => c.id === id);
    const snapshot = () => clips.map((c) => c.fx.slice());

    /* ---------- Monitor ---------- */
    // What the playhead is over: the shot on V1 and the title on V2.
    const at = (track) => clips.find((c) => c.kind === 'video' && c.track === track && ph >= c.start && ph < c.end);

    // An effect stack as CSS: filters, flips, zoom, crop, layers and a move.
    const compose = (names) => {
      const look = { filters: [], sx: 1, sy: 1, zoom: 1, clip: '', layers: new Set(), anim: '' };
      names.forEach((name) => {
        const fx = byName(name).fx;
        if (fx.filter) look.filters.push(fx.filter);
        if (fx.sx) look.sx *= fx.sx;
        if (fx.sy) look.sy *= fx.sy;
        if (fx.zoom) look.zoom *= fx.zoom;
        if (fx.clip) look.clip = fx.clip;
        if (fx.layer) look.layers.add(fx.layer);
        if (fx.anim) look.anim = fx.anim;
      });
      return look;
    };
    const MOVES = ['is-slide', 'is-punch', 'is-shake', 'is-pop'];

    // Mosaic: the shot drawn into a 28 x 16 canvas that CSS blows up into
    // blocks. Draws only while Mosaic is on screen; the poster stands in
    // until the video has a frame.
    const mosaic = q('[data-bt-mosaic]');
    const mctx = mosaic && mosaic.getContext ? mosaic.getContext('2d') : null;
    const posters = {};
    let mosaicOn = false;
    let mosaicRaf = 0;
    let mosaicLast = 0;
    const drawMosaic = (now) => {
      mosaicRaf = 0;
      const base = at(1);
      if (!mosaicOn || !base || !visible || document.hidden) return;
      let drawn = false;
      if (!mosaicLast || now - mosaicLast > 66) {
        mosaicLast = now || 1;
        const video = videos.find((v) => v.dataset.media === base.media);
        let src = null;
        if (video && video.readyState >= 2) src = video;
        else if (video && video.poster) {
          if (!posters[video.poster]) {
            posters[video.poster] = new Image();
            posters[video.poster].src = video.poster;
          }
          if (posters[video.poster].complete && posters[video.poster].naturalWidth) src = posters[video.poster];
        }
        if (src) {
          try {
            mctx.drawImage(src, 0, 0, mosaic.width, mosaic.height);
            drawn = true;
          } catch (e) {
            /* not drawable yet */
          }
        }
      }
      if (!(still.matches && drawn)) mosaicRaf = requestAnimationFrame(drawMosaic);
    };
    const runMosaic = (on) => {
      mosaicOn = on && !!mctx;
      if (mosaicOn && !mosaicRaf) {
        mosaicLast = 0;
        mosaicRaf = requestAnimationFrame(drawMosaic);
      } else if (!mosaicOn && mosaicRaf) {
        cancelAnimationFrame(mosaicRaf);
        mosaicRaf = 0;
      }
    };
    const play = (el, anim, replay) => {
      if (replay && anim && !still.matches) {
        el.classList.remove(...MOVES);
        void el.offsetWidth; // restart the move
        el.classList.add(`is-${anim}`);
      } else if (!anim) el.classList.remove(...MOVES);
    };

    // replay: the clips an effect just landed on, so their move plays
    const renderMonitor = (replay) => {
      const base = at(1);
      const title = at(2);
      gap = !base;
      screen.classList.toggle('is-gap', gap);
      videos.forEach((v) => {
        const on = !!base && v.dataset.media === base.media;
        v.classList.toggle('is-on', on);
        if (on && visible && !document.hidden && !scrubbing) {
          if (v.paused) v.play().catch(() => {});
        } else if (!v.paused) v.pause();
      });
      const look = compose(base ? base.fx : []);
      picture.style.setProperty('--bt-filter', look.filters.length ? look.filters.join(' ') : 'none');
      picture.style.setProperty('--bt-sx', look.sx);
      picture.style.setProperty('--bt-sy', look.sy);
      picture.style.setProperty('--bt-zoom', look.zoom);
      picture.style.setProperty('--bt-clip', look.clip || 'inset(0)');
      ['bars', 'grain', 'glow', 'vignette', 'mosaic'].forEach((l) => screen.classList.toggle(`has-${l}`, look.layers.has(l)));
      screen.classList.toggle('has-shake', look.anim === 'shake');
      runMosaic(look.layers.has('mosaic'));
      play(mover, look.anim, base && replay && replay.includes(base.id));

      if (titleEl) {
        titleEl.classList.toggle('is-on', !!title);
        const t = compose(title ? title.fx : []);
        titleEl.style.setProperty('--bt-tfilter', t.filters.length ? t.filters.join(' ') : 'none');
        titleEl.style.setProperty('--bt-tsx', t.sx);
        titleEl.style.setProperty('--bt-tsy', t.sy);
        titleEl.style.setProperty('--bt-tzoom', t.zoom);
        play(titleMover, t.anim, title && replay && replay.includes(title.id));
      }
    };

    const renderPlayhead = () => {
      body.style.setProperty('--ph', (ph / SPAN).toFixed(4));
      timecodeEl.textContent = timecode(ph);
    };

    // Selecting a clip parks the playhead on it, unless it's already there.
    const park = (clip) => {
      if (ph >= clip.start && ph < clip.end) return;
      ph = clip.start + (clip.end - clip.start) * (clip.title ? 0.5 : 0.35);
      renderPlayhead();
    };

    const showClip = (id) => {
      const clip = clipById(id);
      if (!clip) return;
      const video = clip.kind === 'video' ? clip : clipById(clip.link);
      if (!video) return;
      park(video);
      renderMonitor(false);
    };

    /* ---------- Timeline ---------- */
    const clipHtml = (c) => {
      const cls = ['bt-clip'];
      if (c.kind === 'audio') cls.push('bt-clip--audio');
      if (c.fx.length) cls.push('has-fx');
      const style = `--x:${pct(c.start)}%;--w:${pct(c.end - c.start)}%${c.color ? `;--c:${c.color}` : ''}`;
      const fxList = c.fx.length ? `, ${c.fx.join(', ')}` : '';
      const label = c.label;
      return (
        `<button type="button" class="${cls.join(' ')}" data-clip="${c.id}" style="${style}"` +
        ` aria-pressed="${sel.has(c.id)}" aria-label="${esc(label)}, ${c.kind === 'video' ? 'V' : 'A'}${c.track}${esc(fxList)}"` +
        `${c.kind === 'audio' && c.link ? ' tabindex="-1"' : ''}>` +
        // the name is a plain line: the clips only set the scene
        `<span class="bt-fx">fx</span><span class="bt-clip-name"><i class="bt-ln" style="--w: ${Math.min(64, 20 + label.length * 4)}px"></i></span></button>`
      );
    };

    const rowHtml = (kind, track) => {
      const name = `${kind === 'video' ? 'V' : 'A'}${track}`;
      const lane = clips.filter((c) => c.kind === kind && c.track === track).map(clipHtml).join('');
      const cls = track === 1 ? (kind === 'video' ? ' is-v1' : ' is-a1') : '';
      return `<div class="bt-row${cls}"><span class="bt-head" aria-label="${name}"><i class="bt-ln"></i></span><span class="bt-lane">${lane}</span></div>`;
    };

    const renderTracks = (hit) => {
      const focused = document.activeElement && document.activeElement.dataset ? document.activeElement.dataset.clip : null;
      tracksEl.innerHTML = [rowHtml('video', 2), rowHtml('video', 1), rowHtml('audio', 1), rowHtml('audio', 2)].join('');
      if (hit) hit.forEach((id) => tracksEl.querySelector(`[data-clip="${id}"]`)?.classList.add('is-hit'));
      if (focused) tracksEl.querySelector(`[data-clip="${focused}"]`)?.focus({ preventScroll: true });
    };

    const renderControls = () => {
      undoBtn.disabled = !history.length;
      resetBtn.disabled = !history.length && !clips.some((c) => c.fx.length);
    };

    const select = (id, add) => {
      const clip = clipById(id);
      const ids = [id];
      if (clip.link) ids.push(clip.link); // linked selection, as Premiere does by default
      if (add) {
        const on = !sel.has(id);
        ids.forEach((i) => (on ? sel.add(i) : sel.delete(i)));
      } else sel = new Set(ids);
      showClip(id);
      renderTracks();
    };

    const say = (text, kind) => {
      statusEl.textContent = text;
      statusEl.classList.toggle('is-warn', kind === 'warn');
      statusEl.classList.remove('is-faded');
      statusEl.classList.add('is-fresh');
      window.clearTimeout(statusTimer);
      // it says what just happened, then fades
      statusTimer = window.setTimeout(() => statusEl.classList.add('is-faded'), 2000);
    };

    const showBar = (which) => {
      hintBar.classList.toggle('is-current', which === 'hint');
      doneBar.classList.toggle('is-current', which === 'done');
    };

    /* ---------- BetterFX ---------- */
    const renderResults = () => {
      const query = input.value.trim();
      bar.classList.toggle('is-empty', !input.value);
      if (!query) {
        list = meta.recents.map(byName).filter(Boolean).slice(0, 5);
      } else {
        const ranked = rankEffects(EFFECTS, query, meta);
        const lead = ranked[0] && ranked[0].kind;
        list = lead ? ranked.filter((e) => e.kind === lead).concat(ranked.filter((e) => e.kind !== lead)) : [];
      }
      selected = 0;
      let html = '';
      if (query && !list.length) html = `<div class="bfx-empty">${esc(tr('empty', 'No effects found'))}</div>`;
      else if (!query && list.length) html = `<div class="bfx-cat">${esc(tr('cat_recent', 'RECENT EFFECTS'))}</div>`;
      let prev = null;
      list.forEach((effect, i) => {
        if (query && effect.kind !== prev) {
          html += `<div class="bfx-cat">${esc(tr(`cat_${effect.kind}`, CATEGORY[effect.kind]))}</div>`;
          prev = effect.kind;
        }
        const tag = effect.thirdParty ? tr('tag_third', '3rd party') : effect.kind === 'preset' ? tr('tag_preset', 'custom preset') : '';
        html +=
          `<div class="bfx-row${i === selected ? ' is-selected' : ''}" role="option" aria-selected="${i === selected}" data-row="${i}">` +
          `<span class="bfx-name">${esc(effect.name)}</span>${tag ? `<span class="bfx-tag">${esc(tag)}</span>` : ''}</div>`;
      });
      results.innerHTML = html;
      results.scrollTop = 0;
    };

    const updateSelected = () => {
      results.querySelectorAll('.bfx-row').forEach((row) => {
        const on = Number(row.dataset.row) === selected;
        row.classList.toggle('is-selected', on);
        row.setAttribute('aria-selected', String(on));
        if (on) {
          // keep it in view without scrolling the page
          const top = row.offsetTop;
          const bottom = top + row.offsetHeight;
          if (top < results.scrollTop + 26) results.scrollTop = Math.max(0, top - 26);
          else if (bottom > results.scrollTop + results.clientHeight) results.scrollTop = bottom - results.clientHeight;
        }
      });
    };

    const move = (dir) => {
      if (!list.length) return;
      selected = (selected + dir + list.length) % list.length;
      updateSelected();
    };

    // Where to open: centred on the pointer like the real window, kept inside the workspace.
    let lastPoint = null;
    const place = (point) => {
      const s = stage.getBoundingClientRect();
      const narrow = s.width < 560;
      const w = Math.min(360, s.width - (narrow ? 16 : 20));
      let x = point ? point.x : s.width / 2;
      let y = point ? point.y : s.height * 0.62;
      if (narrow) {
        x = s.width / 2;
        y = 44;
      }
      x = Math.min(Math.max(x, w / 2 + 8), s.width - w / 2 - 8);
      y = Math.min(Math.max(y, 40), s.height - 300);
      bar.style.setProperty('--bx', `${x}px`);
      bar.style.setProperty('--by', `${y}px`);
    };

    const open = (point, fromVisitor) => {
      place(point || lastPoint);
      input.value = '';
      renderResults();
      bar.classList.add('is-open');
      isOpen = true;
      if (fromVisitor) {
        input.tabIndex = 0;
        input.focus({ preventScroll: !touch });
      }
    };

    const close = () => {
      if (!isOpen) return;
      isOpen = false;
      bar.classList.remove('is-open', 'is-typing');
      input.tabIndex = -1;
      if (document.activeElement === input) stage.focus({ preventScroll: true });
    };

    const remember = (name) => {
      meta.recents = [name].concat(meta.recents.filter((n) => n !== name)).slice(0, 10);
      meta.usage[name] = (meta.usage[name] || 0) + 1;
    };

    // Enter: the effect goes onto every selected clip it can go on, and the window hides at once.
    const apply = (effect, how) => {
      close();
      if (!effect) return false;
      const targets = clips.filter((c) => sel.has(c.id) && c.kind === effect.media);
      if (!sel.size) {
        say(tr('no_clip', 'Select a clip on the timeline first'), 'warn');
        return false;
      }
      if (!targets.length) {
        const need = effect.media === 'audio' ? tr('need_audio', 'Select an audio clip for [effect]') : tr('need_video', 'Select a video clip for [effect]');
        say(fillIn(need, { effect: effect.name }), 'warn');
        return false;
      }
      history.push(snapshot());
      if (history.length > 30) history.shift();
      targets.forEach((c) => {
        // a move replaces the clip's earlier move; anything else stacks
        if (effect.fx.anim) c.fx = c.fx.filter((n) => !byName(n).fx.anim);
        c.fx = c.fx.filter((n) => n !== effect.name).concat(effect.name);
      });
      remember(effect.name);
      const video = targets.find((c) => c.kind === 'video');
      if (video && !targets.some((c) => c.kind === 'video' && ph >= c.start && ph < c.end)) park(video);
      renderTracks(targets.map((c) => c.id));
      renderMonitor(targets.map((c) => c.id));
      if (video && !still.matches) {
        screen.classList.remove('is-hit');
        void screen.offsetWidth;
        screen.classList.add('is-hit');
      }
      renderControls();
      const count = targets.length;
      say(
        count === 1
          ? fillIn(tr('applied_one', '[effect] applied to [clip]'), { effect: effect.name, clip: targets[0].label })
          : fillIn(tr('applied_many', '[effect] applied to [count] clips'), { effect: effect.name, count })
      );
      doneName.textContent = effect.name;
      doneText.textContent = how;
      donePill.textContent = fillIn(count === 1 ? tr('clips_one', '[count] clip') : tr('clips_other', '[count] clips'), { count });
      showBar('done');
      window.clearTimeout(doneTimer);
      if (!touring) doneTimer = window.setTimeout(() => showBar('hint'), 4200);
      return true;
    };

    const howFor = (query, dir) => {
      if (query) return fillIn(tr('how_typed', 'Typed “[query]”, pressed Enter.'), { query });
      return dir ? tr('how_recent_arrows', 'Picked from Recent effects with the arrow keys.') : tr('how_recent', 'Picked from Recent effects.');
    };
    let arrowed = false;

    const undo = () => {
      const prev = history.pop();
      if (!prev) return;
      clips.forEach((c, i) => (c.fx = prev[i]));
      renderTracks();
      renderMonitor(false);
      renderControls();
      say(tr('undo', 'Undo'));
    };

    const reset = () => {
      clips = named(CLIPS());
      meta = META();
      history = [];
      sel = new Set();
      ph = 1.5;
      renderPlayhead();
      renderTracks();
      renderMonitor(false);
      renderControls();
      showBar('hint');
      say('');
    };

    /* ---------- The visitor ---------- */
    const touched = () => {
      if (touring) endTour();
      stage.classList.add('is-touched');
    };

    input.addEventListener('input', () => {
      arrowed = false;
      renderResults();
    });

    input.addEventListener('keydown', (event) => {
      if (event.isComposing || event.keyCode === 229) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        arrowed = true;
        move(1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        arrowed = true;
        move(-1);
      } else if (event.key === 'Enter') {
        event.preventDefault();
        apply(list[selected], howFor(input.value.trim(), arrowed));
      } else if (event.key === 'Escape') {
        event.preventDefault();
        close();
      } else if (event.key === ' ' && event.altKey) {
        // ⌥Space again while open: nothing to type
        event.preventDefault();
      }
    });

    // Leaving the field closes the window, as the real one hides when it loses focus.
    input.addEventListener('blur', () => {
      window.setTimeout(() => {
        if (isOpen && !touring && document.activeElement !== input) close();
      }, 120);
    });

    results.addEventListener('pointerdown', (event) => event.preventDefault()); // keep the field focused
    results.addEventListener('pointermove', (event) => {
      const row = event.target.closest('[data-row]');
      if (!row || event.pointerType !== 'mouse') return;
      const i = Number(row.dataset.row);
      if (i !== selected) {
        selected = i;
        updateSelected();
      }
    });
    results.addEventListener('click', (event) => {
      const row = event.target.closest('[data-row]');
      if (!row) return;
      selected = Number(row.dataset.row);
      apply(
        list[selected],
        input.value.trim()
          ? fillIn(tr('how_picked', 'Typed “[query]”, picked it.'), { query: input.value.trim() })
          : tr('how_recent', 'Picked from Recent effects.')
      );
    });

    let lastPointer = 'mouse';
    stage.addEventListener('pointerdown', (event) => {
      lastPointer = event.pointerType;
      const s = stage.getBoundingClientRect();
      lastPoint = { x: event.clientX - s.left, y: event.clientY - s.top };
      if (!event.target.closest('.bfx')) touched();
    });
    stage.addEventListener('pointerenter', () => (pointerInside = true));
    stage.addEventListener('pointerleave', () => (pointerInside = false));
    stage.addEventListener('pointermove', (event) => {
      if (event.pointerType !== 'mouse' || isOpen) return;
      const s = stage.getBoundingClientRect();
      lastPoint = { x: event.clientX - s.left, y: event.clientY - s.top };
    });

    stage.addEventListener('click', (event) => {
      if (touring) return;
      const clip = event.target.closest('[data-clip]');
      if (clip) {
        close();
        return select(Number(clip.dataset.clip), event.shiftKey || event.metaKey || event.ctrlKey || lastPointer === 'touch');
      }
      if (event.target.closest('[data-bt-open]')) {
        touched();
        if (isOpen) return close();
        const r = openBtn.getBoundingClientRect();
        const s = stage.getBoundingClientRect();
        return open({ x: r.left - s.left + r.width / 2, y: r.top - s.top - 220 }, true);
      }
      if (event.target.closest('[data-bt-undo]')) return undo();
      if (event.target.closest('[data-bt-reset]')) return reset();
      if (event.target.closest('.bt-lane') || event.target.closest('.bt-screen')) {
        // a click on empty timeline clears the selection, like Premiere
        if (event.target.closest('.bt-lane')) {
          sel = new Set();
          renderTracks();
        }
        close();
      }
    });

    /* ---------- Scrubbing: drag the playhead or along the ruler ---------- */
    const ruler = q('[data-bt-ruler]');
    const grip = q('[data-bt-grip]');
    const scrubTo = (event) => {
      const rect = ruler.getBoundingClientRect();
      const x = Math.min(Math.max(0, event.clientX - rect.left), rect.width);
      ph = Math.min(SPAN - 0.04, (x / rect.width) * SPAN);
      renderPlayhead();
      // the monitor shows whatever V1 has under the playhead, black between clips
      const clip = at(1);
      renderMonitor(false);
      if (!clip) return;
      const video = videos.find((v) => v.dataset.media === clip.media);
      if (video && Number.isFinite(video.duration) && video.duration > 0) {
        try {
          video.currentTime = ((ph - clip.start) / (clip.end - clip.start)) * video.duration;
        } catch (e) {
          /* not seekable yet */
        }
      }
    };
    if (ruler && grip) {
      [ruler, grip].forEach((el) =>
        el.addEventListener('pointerdown', (event) => {
          if (event.button > 0) return;
          event.preventDefault();
          event.stopPropagation();
          touched();
          close();
          scrubbing = true;
          body.classList.add('is-scrubbing');
          try {
            el.setPointerCapture(event.pointerId);
          } catch (e) {
            /* capture is a nicety */
          }
          scrubTo(event);
          const move = (e) => scrubTo(e);
          const up = () => {
            el.removeEventListener('pointermove', move);
            el.removeEventListener('pointerup', up);
            el.removeEventListener('pointercancel', up);
            scrubbing = false;
            body.classList.remove('is-scrubbing');
            renderMonitor(false);
          };
          el.addEventListener('pointermove', move);
          el.addEventListener('pointerup', up);
          el.addEventListener('pointercancel', up);
        })
      );
    }

    // The shortcut: ⌥Space (Alt+Space) while you're in the workspace. If the
    // real BetterFX is running on this computer it takes ⌥Space first, so
    // the browser never sees it: just typing a letter opens the bar too.
    const inWorkspace = () => {
      const active = document.activeElement;
      if (stage.contains(active)) return true;
      if (!pointerInside) return false;
      // not while the visitor types somewhere else on the page
      return !active || active === document.body || !active.matches('input, textarea, select, [contenteditable]');
    };
    window.addEventListener(
      'keydown',
      (event) => {
        if (!visible || touch || event.defaultPrevented) return;
        if (event.altKey && event.code === 'Space') {
          if (!inWorkspace()) return;
          event.preventDefault();
          touched();
          if (isOpen && document.activeElement === input) return;
          open(null, true);
          return;
        }
        if (isOpen || event.altKey || event.ctrlKey || event.metaKey || event.isComposing) return;
        if (event.key.length !== 1 || !/[\p{L}\p{N}]/u.test(event.key)) return;
        if (!inWorkspace()) return;
        event.preventDefault();
        touched();
        open(null, true);
        input.value = event.key;
        arrowed = false;
        renderResults();
      },
      true
    );

    stage.addEventListener('keydown', (event) => {
      if (event.target === input) return;
      if (event.key === 'Escape') close();
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        touched();
        undo();
      }
    });

    // The chips under the workspace: open BetterFX with that search typed.
    root.querySelectorAll('[data-bt-chip]').forEach((chip) =>
      chip.addEventListener('click', () => {
        touched();
        if (!sel.size) {
          const video = at(1) || clipById(1);
          select(video.id, false);
        }
        open(null, true);
        input.value = chip.dataset.btChip;
        arrowed = false;
        renderResults();
      })
    );

    /* ---------- The tour ---------- */
    const wait = (r, ms) =>
      new Promise((resolve, reject) => {
        window.setTimeout(() => (r === run && touring ? resolve() : reject(STOP)), ms);
      });

    const pointAt = (el, dx = 0.5, dy = 0.5) => {
      const s = stage.getBoundingClientRect();
      const r = el.getBoundingClientRect();
      return { x: r.left - s.left + r.width * dx, y: r.top - s.top + r.height * dy };
    };

    const cursorTo = (p) => {
      cursor.style.setProperty('--cx', `${p.x - 4}px`);
      cursor.style.setProperty('--cy', `${p.y - 2}px`);
    };

    const click = async (r) => {
      cursor.classList.remove('is-click');
      void cursor.offsetWidth;
      cursor.classList.add('is-click');
      await wait(r, 260);
    };

    const pressKeys = async (r, caps, hold = 520) => {
      keysEl.innerHTML = caps.map((c) => `<kbd>${esc(c)}</kbd>`).join('');
      keysEl.classList.add('is-on');
      await wait(r, 220);
      keysEl.querySelectorAll('kbd').forEach((k) => k.classList.add('is-down'));
      await wait(r, hold);
      keysEl.querySelectorAll('kbd').forEach((k) => k.classList.remove('is-down'));
      await wait(r, 160);
      keysEl.classList.remove('is-on');
    };

    const clipEl = (id) => tracksEl.querySelector(`[data-clip="${id}"]`);

    const tourClick = async (r, id, add) => {
      cursorTo(pointAt(clipEl(id), 0.5, 0.55));
      await wait(r, 700);
      await click(r);
      select(id, add);
      await wait(r, 380);
    };

    const tourOpen = async (r) => {
      await pressKeys(r, isMac ? ['⌥', 'Space'] : ['Alt', 'Space']);
      const c = cursor.style;
      open({ x: parseFloat(c.getPropertyValue('--cx')) + 4, y: parseFloat(c.getPropertyValue('--cy')) - 40 }, false);
      bar.classList.add('is-typing');
      await wait(r, 650);
    };

    const tourType = async (r, text) => {
      for (const ch of text) {
        input.value += ch;
        renderResults();
        await wait(r, 190 + Math.random() * 90);
      }
      await wait(r, 650);
    };

    const tourEnter = async (r, how) => {
      await pressKeys(r, ['Enter'], 300);
      bar.classList.remove('is-typing');
      apply(list[selected], how);
      await wait(r, 1700);
    };

    const setDot = (i) =>
      dots.forEach((d, j) => {
        d.classList.toggle('is-current', j === i);
        d.classList.toggle('is-done', j < i);
      });

    const runTour = async () => {
      touring = true;
      const r = ++run;
      stage.classList.add('is-touring');
      tourEl.classList.add('is-touring');
      reset();
      cursorTo({ x: stage.clientWidth * 0.5, y: stage.clientHeight * 0.55 });
      try {
        await wait(r, 500);
        cursor.classList.add('is-on');

        setDot(0);
        await tourClick(r, 1, false);
        await tourOpen(r);
        await tourType(r, 'gb');
        await tourEnter(r, howFor('gb'));

        setDot(1);
        await tourClick(r, 2, false);
        await tourClick(r, 3, true);
        await tourOpen(r);
        await tourType(r, 'bw');
        await tourEnter(r, howFor('bw'));

        setDot(2);
        await tourClick(r, 3, false);
        await tourOpen(r);
        await wait(r, 500);
        const shake = list.findIndex((e) => e.name === 'S_Shake');
        for (let i = 0; i < shake; i += 1) {
          await pressKeys(r, ['↓'], 200);
          move(1);
          await wait(r, 260);
        }
        await wait(r, 400);
        await tourEnter(r, howFor('', true));

        // the title on V2 gets a custom preset
        setDot(3);
        await tourClick(r, 7, false);
        await tourOpen(r);
        await tourType(r, 'pop');
        await tourEnter(r, howFor('pop'));

        setDot(4);
        endTour(true);
      } catch (e) {
        if (e !== STOP) throw e;
      }
    };

    // Hand over: keep what the tour did on the timeline, so there's something to Undo.
    const endTour = (finished) => {
      run += 1;
      touring = false;
      toured = true;
      cursor.classList.remove('is-on');
      keysEl.classList.remove('is-on');
      bar.classList.remove('is-typing');
      close();
      stage.classList.remove('is-touring');
      tourEl.classList.remove('is-touring');
      stage.classList.add('is-ready');
      sel = new Set();
      renderTracks();
      if (!finished) showBar('hint');
      else {
        window.clearTimeout(doneTimer);
        doneTimer = window.setTimeout(() => showBar('hint'), 2600);
      }
    };

    // Leaving mid-tour stops it; it plays again from the start next time.
    const abandonTour = () => {
      run += 1;
      touring = false;
      cursor.classList.remove('is-on');
      keysEl.classList.remove('is-on');
      close();
      stage.classList.remove('is-touring');
      reset();
    };

    tourBtn.addEventListener('click', () => {
      if (touring) {
        endTour(false);
        stage.focus({ preventScroll: true });
      } else {
        stage.classList.remove('is-touched');
        runTour();
      }
    });

    /* ---------- On screen ---------- */
    const wake = () => {
      if (still.matches) {
        if (!toured) {
          toured = true;
          tourEl.classList.remove('is-touring');
          stage.classList.add('is-ready');
        }
      } else if (!toured && !touring) runTour();
      renderMonitor(false);
    };

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        (entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          videos.forEach((v) => (v.preload = 'auto'));
        },
        { rootMargin: '600px 0px' }
      ).observe(stage);

      new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            visible = entry.isIntersecting;
            if (visible) wake();
            else {
              if (touring) abandonTour();
              close();
              renderMonitor(false);
            }
          });
        },
        { threshold: 0.4 }
      ).observe(stage);
    } else {
      visible = true;
      toured = true;
      tourEl.classList.remove('is-touring');
    }

    document.addEventListener('visibilitychange', () => renderMonitor(false));

    renderPlayhead();
    renderTracks();
    renderControls();
  };

  const boot = () => document.querySelectorAll('[data-bt-demo]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  // The theme editor re-renders a section in place.
  document.addEventListener('shopify:section:load', boot);
})();
